<template>
  <div>
    <PageName :title="pageName" :buttons="buttons" />
    <Preloader v-if="userLoading" />
    <Profile v-else :user="user" :info="userInfo" />
    <PasswordModal
      v-if="changePasswordDialog"
      @close="changePasswordDialog = false"
      :request="changePassword"
    />
  </div>
</template>

<script>
import PageName from "@/components/PageName.vue";
import Profile from "@/components/Profile.vue";
import Preloader from "@/components/Preloader.vue";
import PasswordModal from "@/components/Modals/PasswordModal.vue";
import { getDepartment } from "@/api/departments";
import { getUser } from "@/api/users";
import { deleteUser, updateUser, updatePassword } from "@/api/users.js";
import { mapActions, mapState, mapGetters } from "vuex";
// import { phone } from "@/utils/filters/formatFilters";
import { FIO } from "@/utils/filters/stringFilters";
import { simpleDate } from "@/utils/filters/dateFilters";

export default {
  components: {
    Profile,
    PageName,
    Preloader,
    PasswordModal,
  },
  async mounted() {
    await getUser(this.$route.params.id)
      .then(({ data }) => (this.user = data))
      .finally(() => (this.userLoading = false));
    if (this.user.department_id) {
      await getDepartment(this.user.department_id, "heads").then( res => {
        this.heads = res.data.heads
      })
    }
  },
  data() {
    return {
      user: {},
      userLoading: true,
      changePasswordDialog: false,
      heads: []
    };
  },
  computed: {
    ...mapState("departments", ["departments"]),
    ...mapGetters("departments", ["getDepartmentById"]),
    ...mapGetters("users", ["isAdmin", "isSuperAdmin", "isLogist"]),
    ...mapState("users", ["activeUser"]),
    pageName() {
      switch (this.role) {
        case "users":
          return "Пользователь";
        case "admins":
          return "Администратор";
        case "osr-staff":
          return "Сотрудник ОСР";
        case "logisticians":
          return "Логист";
        default:
          return "";
      }
    },
    role() {
      return this.$route.params?.type;
    },
    userId() {
      return this.$route.params?.id;
    },
    isUser() {
      return this.role === "users";
    },
    userInfo() {
      return [
        {
          title: "Дата рождения:",
          text: this.user.birthday
              ? simpleDate(this.user.birthday)
              : "-",
        },
        {
          title: "Email",
          text: this.user.email || "-",
          type: "email"
        },
        {
          title: "Логин",
          text: this.user.login || "-",
        },
        {
          title: "Номер служебного моб. телефона",
          text: this.user.phone ? this.user.phone : "-",
          hide: !this.isUser,
        },
        {
          title: "Тип документа",
          text: this.user.document?.name || "-",
          hide: !this.isUser,
        },
        {
          title: "Номер документа",
          text: this.user.document_number || "-",
          hide: !this.isUser,
        },
        {
          title: "Срок действия документа",
          text: this.user.document_validity
            ? simpleDate(this.user.document_validity)
            : "-",
          hide: !this.isUser,
        },
        {
          title: "Подразделение:",
          text: this.department || "-",
          hide: !this.isUser,
          type: 'department'
        },
        {
          title: "Руководители подразделения:",
          text:
              this.heads
              ?.map((head) => FIO(head.name, head.surname, head.patronymic))
              .join(", ") || "-",
          hide: !this.isUser,
        },
        {
          title: "VIP",
          text:  this.user.is_vip ? 'Да' : "Нет",
        },
      ];
    },
    department() {
      return this.getDepartmentById(this.user?.department_id);
    },
    buttons() {
      return [
        {
          text: "Редактировать",
          icon: "edit",
          event: () => !this.userLoading && this.toEdit(),
          iconWidth: "16px",
          hide: !(this.isAdmin || this.isSuperAdmin || this.isLogist),
        },
        {
          text: "Удалить",
          icon: "delete",
          event: () => !this.userLoading && this.delete(),
          iconWidth: "12px",
          hide: !(this.isAdmin || this.isSuperAdmin || this.isLogist),
        },
        {
          text: "Изменить пароль",
          icon: "lock",
          event: () => (this.changePasswordDialog = true),
          hide: this.userId != this.activeUser?.id,
        },
      ];
    },
  },
  methods: {
    ...mapActions("users", ["getUsers"]),
    delete() {
      const confrimFunc = () => {
        if (this.isUser) {
          deleteUser(this.user.id).then(() => {
            this.getUsers();
            this.$router.push(`/admin-panel/${this.role}`);
          });
        } else {
          // убрать этот блок если нужно не удалять роль а удалять самого пользователя

          const {
            id,
            name,
            surname,
            patronymic,
            department_id,
            email,
            login,
            birthday,
            phone,
            roles,
          } = this.user;

          let rolesFiltered = roles
            .filter((role) => role.name !== this.pageName)
            .map((role) => role.id);

          if (!rolesFiltered.length) {
            rolesFiltered = [2]
          }

          updateUser(
            id,
            name,
            surname,
            patronymic,
            department_id,
            email,
            login,
            birthday,
            phone,
            rolesFiltered
          ).then(() => {
            this.getUsers();
            this.$router.push(`/admin-panel/${this.role}`);
          });
        }
      };

      this.$root.confrim(
        this.isUser
          ? "Вы точно хотите удалить этого пользователя?"
          : `Вы точно хотите удалить у этого пользователя роль "${this.pageName}"?`,
        confrimFunc,
        "Да"
      );
    },
    toEdit() {
      this.$router.push({
        name: "SaveUser",
        params: { type: "users" /* this.role */, user: this.user },
      });
    },
    changePassword(passwordData) {
      updatePassword(
        this.user.id,
        passwordData?.newPassword,
        passwordData?.repeatPassword
      );
    },
  },
  watch: {
    userId() {
      this.userLoading = true;
      getUser(this.$route.params.id)
        .then(({ data }) => (this.user = data))
        .finally(() => (this.userLoading = false));
    },
  },
};
</script>

<style lang="scss" scoped></style>
